import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { getApiBaseUrl } from "../../config/api";
import { DISABILITY_TYPE_OPTIONS, disabilityTypeLabel, hasSpecialNeedsRecord } from "../../utils/specialNeedsConstants";

export default function SpecialNeedsSection({ studentId, student, onUpdated, canEdit = true }) {
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [feedback, setFeedback] = useState("");
    const [form, setForm] = useState({
        disabilityType: "",
        specialNeedsNotes: "",
        supportRequired: "",
    });

    useEffect(() => {
        setForm({
            disabilityType: String(student?.disabilityType || "").trim(),
            specialNeedsNotes: String(student?.specialNeedsNotes || "").trim(),
            supportRequired: String(student?.supportRequired || "").trim(),
        });
    }, [student]);

    const save = async () => {
        if (!studentId) return;
        setSaving(true);
        setError("");
        setFeedback("");
        try {
            const { data } = await axios.put(`${getApiBaseUrl()}/Student/${studentId}`, form);
            if (data?.message && !data?.id && !data?._id) {
                setError(data.message);
                return;
            }
            setFeedback("Special needs record saved.");
            setEditMode(false);
            onUpdated?.(data);
        } catch (err) {
            setError(err.response?.data?.message || "Could not save special needs details.");
        } finally {
            setSaving(false);
        }
    };

    const active = hasSpecialNeedsRecord(student);

    return (
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <AccessibilityNewOutlinedIcon color="primary" />
                    <Box>
                        <Typography variant="subtitle1" fontWeight={800}>
                            Special needs support
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Confidential record for learning support and reasonable adjustments.
                        </Typography>
                    </Box>
                </Stack>
                {active ? <Chip label="Support on file" color="info" size="small" /> : <Chip label="No record" variant="outlined" size="small" />}
            </Stack>

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            ) : null}
            {feedback ? (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFeedback("")}>
                    {feedback}
                </Alert>
            ) : null}

            {editMode ? (
                <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Disability / support category</InputLabel>
                        <Select
                            label="Disability / support category"
                            value={form.disabilityType}
                            onChange={(e) => setForm((prev) => ({ ...prev, disabilityType: e.target.value }))}
                        >
                            {DISABILITY_TYPE_OPTIONS.map((item) => (
                                <MenuItem key={item.value || "none"} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Special needs notes"
                        multiline
                        minRows={3}
                        value={form.specialNeedsNotes}
                        onChange={(e) => setForm((prev) => ({ ...prev, specialNeedsNotes: e.target.value }))}
                        placeholder="Diagnosis, IEP details, classroom adjustments…"
                        fullWidth
                    />
                    <TextField
                        label="Support required"
                        multiline
                        minRows={2}
                        value={form.supportRequired}
                        onChange={(e) => setForm((prev) => ({ ...prev, supportRequired: e.target.value }))}
                        placeholder="e.g. extra time in exams, front-row seating, aide support"
                        fullWidth
                    />
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={save} disabled={saving}>
                            {saving ? "Saving…" : "Save"}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setEditMode(false);
                                setForm({
                                    disabilityType: String(student?.disabilityType || "").trim(),
                                    specialNeedsNotes: String(student?.specialNeedsNotes || "").trim(),
                                    supportRequired: String(student?.supportRequired || "").trim(),
                                });
                            }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Stack>
            ) : (
                <Stack spacing={1.5}>
                    <Typography variant="body2">
                        <strong>Category:</strong> {disabilityTypeLabel(student?.disabilityType)}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Notes:</strong> {student?.specialNeedsNotes?.trim() || "—"}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Support required:</strong> {student?.supportRequired?.trim() || "—"}
                    </Typography>
                    {canEdit ? (
                        <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setEditMode(true)} sx={{ alignSelf: "flex-start", mt: 1 }}>
                            Edit special needs
                        </Button>
                    ) : null}
                </Stack>
            )}
        </Paper>
    );
}
